import Grid from "@mui/material/Grid";
import {
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { InfoOutlined, Search } from "@mui/icons-material";
import React, { useEffect, useMemo, useState } from "react";
import QuestionSummary from "./QuestionSummaryCard";
import { useApiClient } from "../../../api";
import { usePagination } from "../../../hooks/usePagination";
import { AvailableQuestionTypes } from "./QuestionTypePicker";
import { useAuthenticationStore } from "../../../stores/auth";

export const FruitifyQuestionSearch = ({
  onQuestionSelected,
  excludeQuestions = [],
  hideTitle = false,
  specificSubject = null,
  specificLevel = null,
  assignmentCreate = false,
  quiz = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedEducationLevel, setSelectedEducationLevel] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [educationLevels, setEducationLevels] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("Published");
  const [selectedQuestionType, setSelectedQuestionType] = useState("All");
  const ApiClient = useApiClient();
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [debouncedValue, setDebouncedValue] = useState(searchTerm);
  const { user } = useAuthenticationStore();
  const isIndependent = user?.info?.subscriptionStatus === "independent";

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(searchTerm);
    }, 200);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const filters = useMemo(() => {
    // compute subjectIds based on selection
    const subjectIds = selectedSubject
      ? [
          selectedSubject.id,
          ...(selectedSubject.subjectAndChildren?.map((s) => s.id) || []),
        ]
      : subjects.map((subj) => subj.id);

    const educationLevelIds = selectedEducationLevel
      ? [selectedEducationLevel.id]
      : educationLevels.map((level) => level.id);

    return {
      searchString: debouncedValue,
      subjects: subjectIds,
      educationLevels: educationLevelIds,
      fromSameSchool: ownerFilter === "my_schools",
      userOnly: ownerFilter === "mine",
      published: selectedStatus === "Published",
      questionType: selectedQuestionType,
      quizOnly: quiz,
      assignmentOrQuizCreate: assignmentCreate || quiz,
    };
  }, [
    debouncedValue,
    ownerFilter,
    selectedEducationLevel,
    selectedSubject,
    subjects,
    educationLevels,
    selectedStatus,
    selectedQuestionType,
    assignmentCreate,
    quiz,
  ]);

  const {
    options: questions,
    page,
    pageInfo,
    handlePageChange,
  } = usePagination(
    ({ first, after }) =>
      ApiClient.question.getAllQuestions({ first, after, ...filters }),
    20,
    filters,
  );

  const fetchSubjectsAndLevels = async () => {
    try {
      const response = await ApiClient.core.getSubjectsAndLevels();
      if (response) {
        setSubjects(response.subjects);
        if (response.subjects?.length === 1) {
          setSelectedSubject(response.subjects[0]);
        }
        setEducationLevels(response.educationLevels);
      }
    } catch (error) {
      console.error("Failed to fetch subjects and levels", error);
    }
  };

  useEffect(() => {
    if (specificSubject && specificLevel) {
      setSelectedSubject(specificSubject);
      setSelectedEducationLevel(specificLevel);
    } else {
      fetchSubjectsAndLevels();
    }
  }, [specificSubject, specificLevel]);

  const handleStatusChange = (newStatus) => {
    setSelectedStatus(newStatus);
    if (newStatus === "Draft") {
      setOwnerFilter("mine");
    } else {
      setOwnerFilter("all");
    }
  };

  const filteredQuestions = useMemo(() => {
    const excludeIds = new Set(excludeQuestions.map((q) => q.id));
    return questions.filter((q) => !excludeIds.has(q.id));
  }, [questions, excludeQuestions]);

  const dropdownFlexWidthSm = "25%";
  return (
    <Grid
      container
      spacing={2}
      sx={{ width: "100%", mt: 2 }}
      data-testid={"fruitify-question-search"}
    >
      {!hideTitle && (
        <Grid size={12} sx={{ mb: 1 }}>
          <Typography variant="h3">Fruitify Question Hub</Typography>
        </Grid>
      )}

      {/* Status Tabs at top */}
      <Grid size={12}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Tabs
            value={selectedStatus}
            onChange={(e, newValue) => handleStatusChange(newValue)}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="Published" value="Published" />
            <Tab label="Draft" value="Draft" />
          </Tabs>
        </Stack>
      </Grid>
      {selectedStatus === "Draft" && (
        <Box display="flex" alignItems="center" gap={0.5}>
          <InfoOutlined fontSize="small" color="primary" />
          <Typography variant="body2">
            Draft questions are automatically removed after 30 days inactivity
          </Typography>
        </Box>
      )}

      {/* Each tab has the search + results */}
      <Grid size={12} sx={{ mt: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ width: "100%", mb: 2 }}
        >
          <TextField
            label="Search Keyword"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: dropdownFlexWidthSm } }}
          />

          {!specificSubject && (
            <FormControl
              sx={{ width: { xs: "100%", sm: dropdownFlexWidthSm } }}
            >
              <InputLabel id="subject-label">Subject</InputLabel>
              <Select
                labelId="subject-label"
                value={selectedSubject || ""}
                label="Subject"
                onChange={(e) => setSelectedSubject(e.target.value || null)}
                disabled={selectedSubject && subjects.length === 1}
              >
                <MenuItem value="">All</MenuItem>
                {subjects.map((subj) => (
                  <MenuItem key={subj.id} value={subj}>
                    {subj.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {!specificLevel && (
            <FormControl
              sx={{ width: { xs: "100%", sm: dropdownFlexWidthSm } }}
            >
              <InputLabel id="level-label">Level</InputLabel>
              <Select
                labelId="level-label"
                value={selectedEducationLevel || ""}
                label="Level"
                onChange={(e) =>
                  setSelectedEducationLevel(e.target.value || null)
                }
              >
                <MenuItem value="">All</MenuItem>
                {educationLevels.map((level) => (
                  <MenuItem key={level.id} value={level}>
                    {level.level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl sx={{ width: { xs: "100%", sm: dropdownFlexWidthSm } }}>
            <InputLabel id="type-label">Question Type</InputLabel>
            <Select
              labelId="type-label"
              value={selectedQuestionType || ""}
              label="Question Type"
              onChange={(e) => setSelectedQuestionType(e.target.value || null)}
            >
              <MenuItem value="All">All</MenuItem>

              {Object.entries(AvailableQuestionTypes)
                .filter(([, type]) => {
                  if (isIndependent) {
                    // Independent users can only see Long Text questions
                    return type.usedInAssignments;
                  }
                  if (assignmentCreate) {
                    return quiz ? type.usedInQuizzes : type.usedInAssignments;
                  }
                  return true; // not assignment create → include all
                })
                .map(([, { backendName, displayName }]) => (
                  <MenuItem key={backendName} value={backendName}>
                    {displayName}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <FormControl sx={{ width: { xs: "100%", sm: dropdownFlexWidthSm } }}>
            <InputLabel id="owner-label">Owner</InputLabel>
            <Select
              labelId="owner-label"
              value={ownerFilter}
              label="Owner"
              disabled={selectedStatus === "Draft"}
              onChange={(e) => setOwnerFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="mine">My Questions</MenuItem>
              <MenuItem value="my_schools">My School's Questions</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        {/* Render filtered questions for the selected tab (reversed order) */}
        {filteredQuestions.map((question) => (
          <Grid size={12} key={question.id} sx={{ mt: 2 }}>
            <QuestionSummary
              question={question}
              onQuestionSelected={onQuestionSelected}
              assignmentCreate={assignmentCreate}
            />
          </Grid>
        ))}
      </Grid>
      <Box display="flex" justifyContent="center" mt={3}>
        <Pagination
          page={page}
          onChange={handlePageChange}
          count={pageInfo.hasNextPage ? page + 1 : page} // keeps the pagination going
          color="primary"
        />
      </Box>
    </Grid>
  );
};
